import { StyleSheet, Platform } from 'react-native';

// Amarelo institucional, o mesmo da tela de boas-vindas (OnboardingScreen).
//
// ⚠️ REGRA DE USO — não é decoração livre:
//  - sobre o NAVY do topo, pode ser TEXTO (contraste alto);
//  - sobre o BRANCO da folha, só como PREENCHIMENTO com texto escuro em cima.
//    Amarelo como texto no branco dá ~1.7:1 de contraste — ilegível, reprova em
//    qualquer critério de acessibilidade. Por isso o link "Esqueci minha senha"
//    e os rótulos da folha continuam em navy.
const YELLOW = '#F5C518';
const INK = '#0A3768';
// Navy do botão secundário da tela de boas-vindas (`btnSecondary`).
const NAVY_DARK = '#09305C';

export const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: '100%',
        maxWidth: '100%',
        backgroundColor: '#03386C',
        overflow: 'hidden',
    },
    content: {
        flex: 1,
        width: '100%',
        maxWidth: '100%',
        // paddingTop vem inline da tela: insets.top + BRAND_TOP_GAP.
        overflow: 'hidden',
    },
    safeContent: {
        flex: 1,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    backgroundPlaneTop: {
        position: 'absolute',
        top: 0,
        right: -40,
        width: 210,
        height: 260,
        opacity: 0.08,
        backgroundColor: '#FFFFFF',
        transform: [{ rotate: '28deg' }],
    },
    backgroundPlaneBottom: {
        position: 'absolute',
        left: -72,
        bottom: 260,
        width: 220,
        height: 90,
        opacity: 0.08,
        backgroundColor: '#F4F9FD',
        transform: [{ rotate: '-18deg' }],
    },
    dotLayer: {
        position: 'absolute',
        top: 130,
        left: 20,
        right: 20,
        height: 130,
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: 22,
        rowGap: 18,
        opacity: 0.12,
    },
    dot: {
        width: 2,
        height: 2,
        borderRadius: 1,
        backgroundColor: '#FFFFFF',
    },
    topBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 24,
        paddingBottom: 8,
        width: '100%',
        maxWidth: '100%',
    },
    topBarUltraCompact: {
        paddingHorizontal: 20,
        paddingBottom: 3,
    },
    brand: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        minWidth: 0,
    },
    brandTextBlock: {
        flex: 1,
        minWidth: 0,
    },
    // Marca idêntica à da tela de boas-vindas (OnboardingScreen).
    // Proporção 35:38 = a do recorte do logo; não mexer só num dos lados.
    logo: {
        width: 35,
        height: 38,
    },
    logoUltraCompact: {
        width: 29,
        height: 31,
    },
    // ⚠️ `fontFamily` explícito é obrigatório: esta tela usa `AppText`, que
    // injeta `{fontFamily:'Liebling'}` em toda instância — e `fontWeight`
    // sozinho NÃO sintetiza peso em fonte custom. Era por isso que o antigo
    // `fontWeight: '900'` daqui não deixava nada em negrito de fato.
    brandName: {
        color: '#FFFFFF',
        fontFamily: 'Liebling-Bold',
        fontSize: 15,
        letterSpacing: 1.5,
        lineHeight: 18,
        borderBottomWidth: 2,
        borderBottomColor: '#FFFFFF',
        paddingBottom: 3,
        alignSelf: 'flex-start',
    },
    brandNameUltraCompact: {
        fontSize: 13,
        lineHeight: 15,
        paddingBottom: 2,
    },
    brandCaption: {
        color: 'rgba(255,255,255,0.78)',
        fontFamily: 'Liebling-Light',
        fontSize: 11,
        letterSpacing: 0.2,
        lineHeight: 14,
        marginTop: 4,
    },
    brandCaptionUltraCompact: {
        fontSize: 9,
        lineHeight: 11,
        marginTop: 2,
    },
    exitButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        minHeight: 36,
        paddingHorizontal: 6,
        flexShrink: 0,
    },
    exitText: {
        color: 'rgba(255,255,255,0.62)',
        fontSize: 13,
        fontWeight: '700',
    },
    exitTextUltraCompact: {
        fontSize: 11,
    },
    exitChevron: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 22,
        lineHeight: 22,
        fontWeight: '300',
    },
    exitChevronUltraCompact: {
        fontSize: 18,
        lineHeight: 18,
    },
    hero: {
        alignItems: 'center',
        paddingHorizontal: 28,
        paddingTop: 28,
        paddingBottom: 24,
        flexShrink: 0,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    heroCompact: {
        paddingTop: 12,
        paddingBottom: 44,
    },
    heroVeryCompact: {
        paddingTop: 10,
        paddingBottom: 44,
    },
    heroUltraCompact: {
        paddingHorizontal: 22,
        paddingTop: 4,
        paddingBottom: 5,
    },
    heroTitle: {
        color: '#FFFFFF',
        fontSize: 27,
        lineHeight: 32,
        fontWeight: '900',
        textAlign: 'center',
        maxWidth: 320,
    },
    heroTitleCompact: {
        fontSize: 22,
        lineHeight: 26,
    },
    heroTitleVeryCompact: {
        fontSize: 20,
        lineHeight: 24,
    },
    heroTitleUltraCompact: {
        fontSize: 18,
        lineHeight: 21,
        maxWidth: 255,
    },
    heroSubtitle: {
        color: 'rgba(255,255,255,0.62)',
        fontSize: 14,
        lineHeight: 20,
        textAlign: 'center',
        width: '100%',
        maxWidth: 292,
        marginTop: 9,
    },
    heroSubtitleCompact: {
        fontSize: 12.5,
        lineHeight: 17,
        marginTop: 5,
        maxWidth: 280,
    },
    heroSubtitleVeryCompact: {
        fontSize: 12,
        lineHeight: 16,
        marginTop: 4,
    },
    heroSubtitleUltraCompact: {
        fontSize: 10.5,
        lineHeight: 13,
        marginTop: 2,
        maxWidth: 258,
    },
    metricsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        marginTop: 20,
        width: '100%',
        maxWidth: 310,
    },
    metricsRowCompact: {
        marginTop: 10,
        maxWidth: 280,
        gap: 12,
    },
    metricsRowVeryCompact: {
        marginTop: 7,
        gap: 10,
    },
    metricsRowUltraCompact: {
        marginTop: 3,
        gap: 8,
        maxWidth: 250,
    },
    metricItem: {
        flex: 1,
        alignItems: 'center',
        minWidth: 0,
    },
    // Sobre o navy do topo o amarelo pode ser texto — mesmo uso da tela de
    // boas-vindas, onde "E você?" e os ícones dos cards são amarelos.
    metricValue: {
        color: YELLOW,
        fontSize: 19,
        lineHeight: 22,
        fontWeight: '900',
    },
    metricValueCompact: {
        fontSize: 16,
        lineHeight: 18,
    },
    metricValueUltraCompact: {
        fontSize: 14,
        lineHeight: 15,
    },
    metricLabel: {
        color: 'rgba(255,255,255,0.44)',
        fontSize: 10,
        marginTop: 3,
        fontWeight: '800',
        textTransform: 'uppercase',
    },
    metricLabelCompact: {
        fontSize: 9,
        marginTop: 2,
    },
    metricLabelUltraCompact: {
        fontSize: 8,
        marginTop: 1,
    },
    sheet: {
        flex: 1,
        width: '100%',
        maxWidth: '100%',
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 32,
        borderTopRightRadius: 32,
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: -12 },
        shadowOpacity: 0.24,
        shadowRadius: 28,
        elevation: 18,
        overflow: 'hidden',
    },
    sheetHandleWrap: {
        alignItems: 'center',
        paddingTop: 12,
        paddingBottom: 4,
    },
    sheetHandleWrapUltraCompact: {
        paddingTop: 7,
        paddingBottom: 2,
    },
    sheetHandle: {
        width: 42,
        height: 4,
        borderRadius: 2,
        backgroundColor: '#E5E7EB',
    },
    sheetScroll: {
        flex: 1,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    sheetContent: {
        width: '100%',
        maxWidth: '100%',
        paddingHorizontal: 24,
        paddingTop: 8,
        paddingBottom: 56,
    },
    sheetContentCompact: {
        paddingHorizontal: 22,
        paddingTop: 4,
        paddingBottom: 18,
    },
    sheetContentVeryCompact: {
        paddingHorizontal: 20,
        paddingTop: 2,
        paddingBottom: 12,
    },
    sheetContentUltraCompact: {
        paddingHorizontal: 20,
        paddingTop: 0,
        paddingBottom: 8,
    },
    sheetHeader: {
        marginBottom: 12,
    },
    sheetHeaderCompact: {
        marginBottom: 8,
    },
    sheetHeaderUltraCompact: {
        marginBottom: 6,
    },
    eyebrow: {
        color: '#03386C',
        fontSize: 10,
        fontWeight: '900',
        textTransform: 'uppercase',
        letterSpacing: 1.6,
    },
    eyebrowUltraCompact: {
        fontSize: 9,
        letterSpacing: 1.3,
    },
    sheetTitle: {
        color: '#1F2937',
        fontSize: 21,
        lineHeight: 26,
        fontWeight: '900',
        marginTop: 3,
    },
    sheetTitleUltraCompact: {
        fontSize: 18,
        lineHeight: 22,
        marginTop: 1,
    },
    segmentedControl: {
        flexDirection: 'row',
        backgroundColor: '#F1F5F9',
        borderRadius: 18,
        padding: 4,
        gap: 4,
        marginBottom: 14,
        width: '100%',
        maxWidth: '100%',
    },
    segmentedControlCompact: {
        marginBottom: 10,
    },
    segmentedControlUltraCompact: {
        borderRadius: 14,
        padding: 3,
        gap: 3,
        marginBottom: 7,
    },
    segmentButton: {
        flex: 1,
        height: 42,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
    },
    segmentButtonCompact: {
        height: 36,
    },
    segmentButtonUltraCompact: {
        height: 32,
        borderRadius: 12,
    },
    segmentPill: {
        position: 'absolute',
        top: 4,
        left: 4,
        height: 42,
        borderRadius: 14,
        backgroundColor: '#FFFFFF',
        shadowColor: '#03386C',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.12,
        shadowRadius: 8,
        elevation: 3,
    },
    segmentPillCompact: {
        height: 36,
    },
    segmentPillUltraCompact: {
        top: 3,
        left: 3,
        height: 32,
        borderRadius: 12,
        shadowOpacity: 0.08,
        shadowRadius: 6,
    },
    segmentText: {
        color: '#6B7280',
        fontSize: 14,
        fontWeight: '800',
    },
    segmentTextUltraCompact: {
        fontSize: 12.5,
    },
    segmentTextActive: {
        color: '#03386C',
    },
    field: {
        marginBottom: 10,
    },
    fieldCompact: {
        marginBottom: 7,
    },
    fieldUltraCompact: {
        marginBottom: 5,
    },
    fieldLabel: {
        color: '#4B5563',
        fontSize: 12,
        fontWeight: '800',
        marginBottom: 6,
        marginLeft: 2,
    },
    fieldLabelCompact: {
        fontSize: 11,
        marginBottom: 4,
    },
    fieldLabelUltraCompact: {
        fontSize: 10.5,
        marginBottom: 3,
    },
    fieldControl: {
        width: '100%',
        maxWidth: '100%',
        minHeight: 50,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        backgroundColor: '#F8FAFC',
        flexDirection: 'row',
        alignItems: 'center',
    },
    fieldControlCompact: {
        minHeight: 42,
        borderRadius: 12,
    },
    fieldControlUltraCompact: {
        minHeight: 38,
        borderRadius: 12,
    },
    fieldInput: {
        flex: 1,
        minHeight: 50,
        color: '#111827',
        fontSize: 15,
        fontWeight: '600',
        paddingHorizontal: 15,
        paddingVertical: Platform.OS === 'ios' ? 14 : 10,
        textAlignVertical: 'center',
    },
    fieldInputCompact: {
        minHeight: 42,
        fontSize: 14,
        paddingHorizontal: 13,
        paddingVertical: Platform.OS === 'ios' ? 10 : 7,
    },
    fieldInputUltraCompact: {
        minHeight: 38,
        fontSize: 13,
        paddingHorizontal: 12,
        paddingVertical: Platform.OS === 'ios' ? 8 : 5,
    },
    fieldInputWithAction: {
        paddingRight: 8,
    },
    passwordAction: {
        alignSelf: 'stretch',
        justifyContent: 'center',
        paddingHorizontal: 14,
        flexShrink: 0,
    },
    passwordActionUltraCompact: {
        paddingHorizontal: 12,
    },
    passwordActionText: {
        color: '#03386C',
        fontSize: 12,
        fontWeight: '900',
    },
    passwordActionTextUltraCompact: {
        fontSize: 11,
    },
    passwordRules: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: -2,
        marginBottom: 10,
    },
    passwordRulePill: {
        borderRadius: 999,
        backgroundColor: '#F3F4F6',
        paddingHorizontal: 10,
        paddingVertical: 5,
    },
    passwordRulePillValid: {
        backgroundColor: '#E3F0FA',
    },
    passwordRuleText: {
        color: '#6B7280',
        fontSize: 11,
        fontWeight: '800',
    },
    passwordRuleTextValid: {
        color: '#03386C',
    },
    forgotPassword: {
        alignSelf: 'flex-end',
        marginTop: -1,
        marginBottom: 12,
        minHeight: 28,
        justifyContent: 'center',
    },
    forgotPasswordUltraCompact: {
        minHeight: 22,
        marginBottom: 8,
    },
    forgotPasswordText: {
        color: '#03386C',
        fontSize: 13,
        fontWeight: '800',
    },
    forgotPasswordTextUltraCompact: {
        fontSize: 11.5,
    },
    forgotModalCancel: {
        marginTop: 14,
        paddingVertical: 6,
    },
    // Ação primária em amarelo com texto escuro — o MESMO tratamento do botão
    // "Criar conta" da tela de boas-vindas. É o que dá continuidade: o botão
    // amarelo que o usuário acabou de tocar reaparece aqui como a ação principal.
    // Espelha o botão "Criar conta" da tela de boas-vindas (OnboardingScreen,
    // `btn` + `btnPrimary` + `btnTextPrimary`): mesma altura, mesmos raios
    // assimétricos — o canto inferior esquerdo reto é a assinatura do design,
    // não um descuido — e mesma tipografia.
    mainButton: {
        minHeight: 52,
        borderTopLeftRadius: 26.388,
        borderTopRightRadius: 26.388,
        borderBottomRightRadius: 26.388,
        borderBottomLeftRadius: 0,
        backgroundColor: YELLOW,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 8,
        shadowColor: YELLOW,
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.38,
        shadowRadius: 14,
        elevation: 5,
    },
    mainButtonCompact: {
        minHeight: 44,
        shadowOpacity: 0.22,
        shadowRadius: 10,
    },
    mainButtonUltraCompact: {
        minHeight: 40,
        shadowOpacity: 0.16,
        shadowRadius: 8,
    },
    mainButtonDisabled: {
        opacity: 0.72,
    },
    // `fontFamily` explícito: o `fontWeight: '900'` que estava aqui não produzia
    // negrito nenhum — AppText injeta a Liebling regular e peso não sintetiza em
    // fonte custom.
    mainButtonText: {
        color: INK,
        fontFamily: 'Liebling-Bold',
        fontSize: 17,
        letterSpacing: 0.3,
    },
    mainButtonTextUltraCompact: {
        fontSize: 15,
    },
    dividerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginVertical: 14,
    },
    dividerRowCompact: {
        marginVertical: 9,
    },
    dividerRowVeryCompact: {
        marginVertical: 7,
    },
    dividerRowUltraCompact: {
        gap: 8,
        marginVertical: 6,
    },
    dividerLine: {
        flex: 1,
        height: 1,
        backgroundColor: '#E5E7EB',
    },
    dividerText: {
        color: '#9CA3AF',
        fontSize: 12,
        fontWeight: '800',
    },
    // Espelha o botão "Entrar" da tela de boas-vindas (`btnSecondary`): navy
    // escuro e os raios ESPELHADOS em relação ao primário — aqui o canto reto é
    // o inferior DIREITO, e é assim no design. A borda clara de 1.5 saiu: ela
    // existia para destacar um botão branco sobre folha branca; sobre o navy
    // viraria um contorno claro sem função.
    socialButton: {
        minHeight: 52,
        borderTopLeftRadius: 26.388,
        borderTopRightRadius: 26.388,
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 26.39,
        backgroundColor: NAVY_DARK,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        paddingHorizontal: 16,
        shadowColor: NAVY_DARK,
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.28,
        shadowRadius: 12,
        elevation: 4,
    },
    socialButtonCompact: {
        minHeight: 44,
        gap: 10,
        shadowOpacity: 0.18,
        shadowRadius: 9,
    },
    socialButtonUltraCompact: {
        minHeight: 40,
        gap: 8,
        paddingHorizontal: 12,
        shadowOpacity: 0.14,
        shadowRadius: 7,
    },
    socialIconBadge: {
        width: 28,
        height: 28,
        borderRadius: 8,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
    },
    socialIconBadgeUltraCompact: {
        width: 24,
        height: 24,
        borderRadius: 7,
    },
    // Branco sobre o navy, mesma tipografia do `btnText` da tela de boas-vindas.
    // O badge branco atrás do "G" fica: é o tratamento que as próprias diretrizes
    // do Google indicam para fundo escuro, e mantém o ícone de 4 cores legível.
    socialButtonText: {
        color: '#FFFFFF',
        fontFamily: 'Liebling-Medium',
        fontSize: 17,
        letterSpacing: 0.3,
    },
    socialButtonTextUltraCompact: {
        fontSize: 14.5,
    },
    footer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        marginTop: 18,
    },
    footerCompact: {
        marginTop: 10,
    },
    footerVeryCompact: {
        marginTop: 8,
    },
    footerUltraCompact: {
        marginTop: 5,
        gap: 4,
    },
    footerText: {
        color: '#6B7280',
        fontSize: 13,
        fontWeight: '600',
    },
    footerTextUltraCompact: {
        fontSize: 11,
    },
    footerAction: {
        color: '#03386C',
        fontSize: 13,
        fontWeight: '900',
    },
    footerActionUltraCompact: {
        fontSize: 11,
    },
    legalLinks: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginTop: 12,
        maxWidth: '100%',
    },
    legalLinksCompact: {
        marginTop: 7,
        gap: 6,
    },
    legalLinksVeryCompact: {
        marginTop: 5,
    },
    legalLinksUltraCompact: {
        marginTop: 3,
        gap: 5,
    },
    legalText: {
        color: '#9CA3AF',
        fontSize: 11,
        fontWeight: '700',
    },
    legalTextUltraCompact: {
        fontSize: 9.5,
    },
    legalSeparator: {
        color: '#D1D5DB',
        fontSize: 11,
    },
    successOverlay: {
        flex: 1,
        backgroundColor: 'rgba(17, 24, 39, 0.58)',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 26,
    },
    successCard: {
        width: '100%',
        maxWidth: 360,
        borderRadius: 24,
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 22,
        paddingTop: 24,
        paddingBottom: 20,
        alignItems: 'center',
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 18 },
        shadowOpacity: 0.22,
        shadowRadius: 28,
        elevation: 16,
    },
    successIconOuter: {
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: '#E3F0FA',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    successIconInner: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: '#03386C',
        alignItems: 'center',
        justifyContent: 'center',
    },
    successIconText: {
        color: '#FFFFFF',
        fontSize: 13,
        lineHeight: 16,
        fontWeight: '900',
    },
    successTitle: {
        color: '#111827',
        fontSize: 23,
        lineHeight: 28,
        fontWeight: '900',
        textAlign: 'center',
    },
    successMessage: {
        color: '#6B7280',
        fontSize: 14,
        lineHeight: 20,
        fontWeight: '600',
        textAlign: 'center',
        marginTop: 8,
    },
    successEmailPill: {
        maxWidth: '100%',
        borderRadius: 999,
        backgroundColor: '#EFF6FC',
        borderWidth: 1,
        borderColor: '#C6DDF0',
        paddingHorizontal: 14,
        paddingVertical: 8,
        marginTop: 16,
    },
    successEmailText: {
        color: '#03386C',
        fontSize: 13,
        fontWeight: '800',
    },
    successButton: {
        width: '100%',
        minHeight: 50,
        borderRadius: 14,
        backgroundColor: '#03386C',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 20,
        shadowColor: '#03386C',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.22,
        shadowRadius: 14,
        elevation: 5,
    },
    successButtonText: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '900',
    },
});
